import React from "react"
import * as Yup from "yup"
import {Formik} from "formik"
import Dropdown from "../../../Address/Dropdown"
import TextField from "../../../Address/Textfield"
import Card from "../../../../containers/Card/Card"
import DateField from "../../../DateField/DateField"
import AddressComponent from "../../../Address/Address"
import {countries} from "../../../../resources/countries"
import {US_COUNTRY_CODE} from "../../../../resources/address"
import {
	AnnualIncome,
	BusinessVertical,
	IndustryType,
	Occupation,
	SourceOfIncome,
	SoleProprietorshipAnnualRevenue,
	SoleProprietorshipNumberOfEmployees,
} from "../../../../resources/application"
import {Address, FullName, OkDocument, Phone} from "../../../../resources/common"
import LeftRightSlots from "../../../../containers/LeftRightSlots/LeftRightSlots"
import IdentificationTypeSelect from "../../../IdentificationTypeSelect/ApplicationIdentificationNumber"
import PhoneNumber from "../../../Address/PhoneNumber"
import CheckboxField from "../../../CheckboxField/CheckboxField"
import reevaluateIndividualProductApplicationValidationSchema from "./reevaluateIndividualProductApplicationValidationSchema"
import {IndividualProductApplication} from "../../../../resources/productApplication"

export interface ReevaluatedIndividualProductApplicationData {
	fullName: FullName
	address: Address
	ssn?: string
	nationality?: string
	passport?: string
	matriculaConsular?: string
	dateOfBirth: string | Date
	email?: string
	phone: Phone
	ein?: string
	dba?: string
	industry?: IndustryType | ""
	occupation?: Occupation | ""
	annualIncome?: AnnualIncome | ""
	sourceOfIncome?: SourceOfIncome | ""
	annualRevenue?: SoleProprietorshipAnnualRevenue | ""
	numberOfEmployees?: SoleProprietorshipNumberOfEmployees | ""
	businessVertical?: BusinessVertical | ""
	website?: string | null
}

export default function ReevaluateIndividualProductApplicationForm(props: {
	application: OkDocument<IndividualProductApplication>
	submitCallback: (values: ReevaluatedIndividualProductApplicationData) => void
	formId: string
	disableSensitiveFields?: boolean
}) {
	const {application, submitCallback, formId, disableSensitiveFields = false} = props
	const {data: individual} = application
	const isSoleProp = individual.attributes.soleProprietorship

	const validationSchema = Yup.object().shape(reevaluateIndividualProductApplicationValidationSchema)

	const initialFormValues = {
		fullName: {
			first: individual.attributes.fullName.first ?? "",
			last: individual.attributes.fullName.last ?? "",
		},
		address: {
			street: individual.attributes.address?.street ?? "",
			street2: individual.attributes.address?.street2 ?? "",
			city: individual.attributes.address?.city ?? "",
			state: individual.attributes.address?.state ?? "",
			postalCode: individual.attributes.address?.postalCode ?? "",
			country: individual.attributes.address?.country ?? US_COUNTRY_CODE,
		},
		ssn: individual.attributes.ssn ?? "",
		nationality: individual.attributes.nationality ?? "US",
		passport: individual.attributes.passport ?? "",
		matriculaConsular: individual.attributes.matriculaConsular ?? "",
		dateOfBirth: individual.attributes.dateOfBirth ?? "",
		identificationType: individual.attributes.ssn
			? "ssn"
			: individual.attributes.matriculaConsular
			? "matriculaConsular"
			: "passport",
		ein: individual.attributes.ein ?? undefined,
		dba: individual.attributes.dba ?? undefined,
		phone: individual.attributes.phone,
		email: individual.attributes.email ?? undefined,
		website: individual.attributes.website !== undefined ? individual.attributes.website : undefined,
		industry: individual.attributes.industry ?? undefined,
		occupation: individual.attributes.occupation ?? undefined,
		annualIncome: individual.attributes.annualIncome ?? undefined,
		sourceOfIncome: individual.attributes.sourceOfIncome ?? undefined,
		annualRevenue: individual.attributes.annualRevenue ?? undefined,
		numberOfEmployees: individual.attributes.numberOfEmployees ?? undefined,
		businessVertical: individual.attributes.businessVertical ?? undefined,
	}

	return (
		<Card className="reevaluate-form" title="Individual Information">
			<Formik
				initialValues={initialFormValues}
				validationSchema={validationSchema}
				onSubmit={({identificationType: _, ...values}) =>
					submitCallback({
						...values,
						nationality: values.nationality || undefined,
						email: values.email || undefined,
						ein: values.ein || undefined,
						dba: values.dba || undefined,
						website: values.website === null ? null : values.website || undefined,
						industry: values.industry || undefined,
						occupation: values.occupation || undefined,
						annualIncome: values.annualIncome || undefined,
						sourceOfIncome: values.sourceOfIncome || undefined,
						annualRevenue: values.annualRevenue || undefined,
						numberOfEmployees: values.numberOfEmployees || undefined,
						businessVertical: values.businessVertical || undefined,
					})
				}
			>
				{({values, handleSubmit, handleChange, setFieldValue, handleBlur}) => {
					return (
						<form noValidate onSubmit={handleSubmit} id={formId}>
							<LeftRightSlots
								left={
									<>
										<TextField
											label="First Name"
											placeholder="Add first name"
											name="fullName.first"
											value={values.fullName.first}
											onChange={handleChange}
											onBlur={handleBlur}
											disabled={disableSensitiveFields}
										/>
										<TextField
											label="Last Name"
											placeholder="Add last name"
											name="fullName.last"
											value={values.fullName.last}
											onChange={handleChange}
											onBlur={handleBlur}
											disabled={disableSensitiveFields}
										/>

										<IdentificationTypeSelect
											value={values.identificationType}
											onChange={handleChange}
											label="Document Type"
											disabled={disableSensitiveFields}
										/>

										{values.identificationType === "matriculaConsular" && (
											<TextField
												label="Document ID #"
												placeholder="Enter Matricula Consular"
												name="matriculaConsular"
												value={values.matriculaConsular}
												onChange={(value) => {
													handleChange(value)
													setFieldValue("ssn", "")
													setFieldValue("passport", "")
												}}
												onBlur={handleBlur}
												disabled={disableSensitiveFields}
											/>
										)}

										{values.identificationType === "ssn" && (
											<>
												<Dropdown
													name="nationality"
													label="Nationality"
													value={values.nationality}
													onChange={handleChange}
													onBlur={handleBlur}
													disabled={disableSensitiveFields}
												>
													{Object.keys(countries).map((countryCode) => (
														<option key={countryCode} value={countryCode}>
															{countries[countryCode]}
														</option>
													))}
												</Dropdown>
												<TextField
													label="Document ID #"
													placeholder="Enter SSN"
													name="ssn"
													value={values.ssn}
													onChange={(value) => {
														handleChange(value)
														setFieldValue("matriculaConsular", "")
														setFieldValue("passport", "")
													}}
													onBlur={handleBlur}
													disabled={disableSensitiveFields}
												/>
											</>
										)}

										{values.identificationType === "passport" ? (
											<>
												<Dropdown
													name="nationality"
													label="Nationality"
													value={values.nationality}
													onChange={handleChange}
													onBlur={handleBlur}
													disabled={disableSensitiveFields}
												>
													{Object.keys(countries).map((countryCode) => (
														<option key={countryCode} value={countryCode}>
															{countries[countryCode]}
														</option>
													))}
												</Dropdown>

												<TextField
													label="Document ID #"
													placeholder="Enter Passport"
													name="passport"
													value={values.passport}
													onChange={(value) => {
														handleChange(value)
														setFieldValue("matriculaConsular", "")
														setFieldValue("ssn", "")
													}}
													onBlur={handleBlur}
													disabled={disableSensitiveFields}
												/>
											</>
										) : null}

										<DateField
											name="dateOfBirth"
											label="Birth Date"
											value={values.dateOfBirth as string}
											onChange={handleChange}
											onBlur={handleBlur}
											disabled={disableSensitiveFields}
										/>

										{isSoleProp && (
											<>
												<TextField
													label="Doing Business As"
													placeholder="Enter DBA"
													name="dba"
													value={values.dba ?? ""}
													onChange={handleChange}
													onBlur={handleBlur}
												/>
												<TextField
													label="EIN"
													placeholder="Enter EIN"
													name="ein"
													value={values.ein ?? ""}
													onChange={(value) => {
														handleChange(value)
													}}
													onBlur={handleBlur}
													disabled={disableSensitiveFields}
												/>
												<Dropdown
													name="annualRevenue"
													label="Annual Revenue"
													value={values.annualRevenue ?? ""}
													onChange={handleChange}
													onBlur={handleBlur}
												>
													{values.annualRevenue ? <React.Fragment /> : <option value="" />}
													{Object.entries(SoleProprietorshipAnnualRevenue).map(([key, value]) => (
														<option key={key} value={key}>
															{value}
														</option>
													))}
												</Dropdown>
												<Dropdown
													name="numberOfEmployees"
													label="Number Of Employees"
													value={values.numberOfEmployees ?? ""}
													onChange={handleChange}
													onBlur={handleBlur}
												>
													{values.numberOfEmployees ? <React.Fragment /> : <option value="" />}
													{Object.entries(SoleProprietorshipNumberOfEmployees).map(([key, value]) => (
														<option key={key} value={key}>
															{value}
														</option>
													))}
												</Dropdown>
											</>
										)}

										<TextField
											label="Email"
											placeholder="Enter Email"
											name="email"
											value={values.email ?? ""}
											onChange={(value) => {
												handleChange(value)
											}}
											onBlur={handleBlur}
										/>

										<div className={"website"}>
											<TextField
												label="Website"
												placeholder="Enter website"
												name="website"
												value={values.website ?? ""}
												onChange={(value) => {
													handleChange(value)
												}}
												onBlur={handleBlur}
												disabled={values.website === null}
											/>
											<CheckboxField
												name="noWebsite"
												label="This individual has no website"
												checked={values.website === null}
												onBlur={handleBlur}
												onChange={(value) => {
													const isChecked = value.target.checked
													if (isChecked) {
														setFieldValue("website", null, true)
													} else {
														setFieldValue("website", individual.attributes.website ?? "", true)
													}
												}}
											/>
										</div>

										<PhoneNumber
											name="phoneNumber"
											label="Phone Number"
											countryCodeName="phone.countryCode"
											phoneNumberName="phone.number"
											countryCodeValue={values.phone.countryCode}
											phoneNumberValue={values.phone.number}
											countryCodeOnChange={(value) => {
												setFieldValue("phone.countryCode", value)
											}}
											phoneNumberOnChange={(value) => {
												setFieldValue("phone.number", value)
											}}
											countryCodeOnBlur={handleBlur}
											phoneNumberOnBlur={handleBlur}
											infoTooltipText="Enter the country code followed by the phone number."
										/>

										<Dropdown
											name="industry"
											label="Industry"
											value={values.industry ?? ""}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{values.industry ? <React.Fragment /> : <option value="" />}
											{Object.entries(IndustryType).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>

										<Dropdown
											name="occupation"
											label="Occupation"
											value={values.occupation ?? ""}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{values.occupation ? <React.Fragment /> : <option value="" />}
											{Object.entries(Occupation).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>

										<Dropdown
											name="annualIncome"
											label="Annual Income"
											value={values.annualIncome ?? ""}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{values.annualIncome ? <React.Fragment /> : <option value="" />}
											{Object.entries(AnnualIncome).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>

										<Dropdown
											name="sourceOfIncome"
											label="Source Of Income"
											value={values.sourceOfIncome ?? ""}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{values.sourceOfIncome ? <React.Fragment /> : <option value="" />}
											{Object.entries(SourceOfIncome).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>

										<Dropdown
											name="businessVertical"
											label="Business Vertical"
											value={values.businessVertical ?? ""}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											{values.businessVertical ? <React.Fragment /> : <option value="" />}
											{Object.entries(BusinessVertical).map(([key, value]) => (
												<option key={key} value={key}>
													{value}
												</option>
											))}
										</Dropdown>
									</>
								}
								right={
									<AddressComponent
										closed={false}
										namePrefix="address."
										street={values.address.street}
										street2={values.address.street2}
										city={values.address.city}
										country={values.address.country || ""}
										state={values.address.state}
										postalCode={values.address.postalCode}
										isUsOnlyAddress={false}
										handleChange={handleChange}
										handleBlur={handleBlur}
										setFieldValue={setFieldValue}
									/>
								}
							/>
						</form>
					)
				}}
			</Formik>
		</Card>
	)
}
